import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  User,
  Mail,
  Shield,
  Calendar,
  Trash2,
  Copy,
  Briefcase,
} from "lucide-react";
import { apiClient } from "../../utils/apiClient/index.ts";
import { useAlert } from "../../hooks/useAlert";

import type { MemberDetail } from "../../types";
import Button from "../../components/common/ui/button/Button";
import { useAuth } from "../../contexts/AuthContext";
import Breadcrumb from "../../components/common/common/Breadcrumb";
import { useOrg } from "../../contexts/OrgContext.tsx";
import { Card } from "../../components/common/Card/Card.tsx";
import { OrganizationRole } from "@secure-caseworker/shared";

export const MemberDetailPage: React.FC = () => {
  const { orgId, userId } = useParams<{ orgId: string; userId: string }>();
  const navigate = useNavigate();
  const { setAlert } = useAlert();
  const { currentUser } = useAuth();
  const [memberDetail, setMemberDetail] = useState<MemberDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const { navigateOrgUrl, navigateOrg } = useOrg();

  useEffect(() => {
    if (orgId && userId) {
      loadMemberDetail();
    }
  }, [orgId, userId]);

  const loadMemberDetail = async () => {
    if (!orgId || !userId) return;

    setLoading(true);
    try {
      const detail = await apiClient.getMemberDetail(orgId, userId);
      setMemberDetail(detail);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error
            ? error.message
            : "Failed to load member details",
      });
      navigate(`/organization`);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleUpdate = async (newRole: OrganizationRole) => {
    if (!orgId || !userId || !memberDetail) return;

    setUpdating(true);
    try {
      await apiClient.updateMemberRole(orgId, userId, newRole);
      setAlert({
        variant: "success",
        title: "Success",
        message: "Member role updated successfully",
      });
      await loadMemberDetail();
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to update role",
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!orgId || !userId || !memberDetail) return;

    const confirmMessage = memberDetail.isPending
      ? "Are you sure you want to revoke this invitation?"
      : "Are you sure you want to remove this member from the organization?";

    if (!confirm(confirmMessage)) return;

    setUpdating(true);
    try {
      if (memberDetail.isPending && memberDetail.invitationId) {
        await apiClient.revokeInvitation(orgId, memberDetail.invitationId);
        setAlert({
          variant: "success",
          title: "Success",
          message: "Invitation revoked successfully",
        });
      } else {
        await apiClient.removeOrganizationMember(orgId, userId);
        setAlert({
          variant: "success",
          title: "Success",
          message: "Member removed successfully",
        });
      }
      navigate(`/organization}`);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error
            ? error.message
            : memberDetail.isPending
              ? "Failed to revoke invitation"
              : "Failed to remove member",
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleCopyInvitationLink = () => {
    if (!memberDetail?.invitationId) return;

    // Note: In production, you'd need to fetch the actual token
    setAlert({
      variant: "info",
      title: "Info",
      message:
        "Copy invitation link from the organization page where the full token is available",
    });
  };

  const handleCaseClick = (caseId: string) => {
    navigateOrg(`/cases/${caseId}`);
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!memberDetail) {
    return null;
  }

  const orgRole = currentUser?.organizations.find(
    (org) => org.id === orgId,
  )?.role;
  const isViewingSelf = currentUser?.id === memberDetail.userId;
  const canEditRole = orgRole === "admin" && !isViewingSelf;
  const canDelete = orgRole === "admin";

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <Breadcrumb
        items={[
          { label: "Organization", path: navigateOrgUrl("") },
          { label: memberDetail.fullName || memberDetail.email || "Member" },
        ]}
      />

      {/* Header */}
      <div>
        <h1 className="mb-1 text-title-md2 font-semibold text-black dark:text-white">
          Member Details
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          View and manage member information
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Member Info Card */}
        <div className="lg:col-span-2">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="mb-6 flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/30">
                  <User
                    size={32}
                    className="text-brand-600 dark:text-brand-400"
                  />
                </div>
                <div>
                  <h2 className="mb-1 text-title-md font-semibold text-black dark:text-white">
                    {memberDetail.fullName || "Pending User"}
                  </h2>
                  {memberDetail.isPending && (
                    <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-sm font-medium text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                      Pending Invitation
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {/* Email */}
              <Card>
                <div className="flex items-center gap-3">
                  <Mail
                    size={20}
                    className="text-gray-500 dark:text-gray-400"
                  />
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Email
                    </div>
                    <div className="text-sm text-black dark:text-white">
                      {memberDetail.email}
                    </div>
                  </div>
                </div>
              </Card>

              {/* Role */}
              <Card>
                <div className="flex items-center gap-3">
                  <Shield
                    size={20}
                    className="text-gray-500 dark:text-gray-400"
                  />
                  <div className="flex-1">
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Role
                    </div>
                    {canEditRole ? (
                      <select
                        value={memberDetail.role}
                        onChange={(e) =>
                          handleRoleUpdate(e.target.value as OrganizationRole)
                        }
                        disabled={updating}
                        className="mt-1 h-11 w-64 rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                      >
                        <option value="admin">Admin</option>
                        <option value="staff">Staff</option>
                        <option value="client">Client</option>
                      </select>
                    ) : (
                      <div className="mt-1 text-sm capitalize text-black dark:text-white">
                        {memberDetail.role}
                      </div>
                    )}
                  </div>
                </div>
              </Card>

              {/* Created Date */}
              <Card>
                <div className="flex items-center gap-3">
                  <Calendar
                    size={20}
                    className="text-gray-500 dark:text-gray-400"
                  />
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      {memberDetail.isPending ? "Invited" : "Joined"}
                    </div>
                    <div className="text-sm text-black dark:text-white">
                      {memberDetail.createdAt}
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Actions */}
            {canDelete && (
              <div className="mt-6 flex gap-3">
                {memberDetail.isPending && (
                  <Button
                    variant="outline"
                    onClick={handleCopyInvitationLink}
                    disabled={updating}
                  >
                    <Copy size={16} className="mr-2" />
                    Copy Invitation Link
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={handleDelete}
                  disabled={updating}
                  className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
                >
                  <Trash2 size={16} className="mr-2" />
                  {memberDetail.isPending
                    ? "Revoke Invitation"
                    : "Remove Member"}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Assigned Cases Card */}
        <div className="lg:col-span-1">
          <Card>
            <h3 className="mb-4 flex items-center gap-2 text-title-sm font-semibold text-black dark:text-white">
              <Briefcase size={20} />
              Assigned Cases
            </h3>

            {memberDetail.assignedCases.length === 0 ? (
              <div className="py-8 text-center">
                <Briefcase
                  size={48}
                  className="mx-auto mb-3 opacity-30 text-gray-400"
                />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  No cases assigned yet
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {memberDetail.assignedCases.map((caseItem) => (
                  <Card
                    variant="info"
                    onClick={() => handleCaseClick(caseItem.caseId)}
                  >
                    <div className="mb-2 font-medium text-black dark:text-white">
                      {caseItem.caseTitle}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      Assigned{" "}
                      {new Date(caseItem.assignedAt).toLocaleDateString()}
                    </div>
                  </Card>
                ))}
              </div>
            )}

            <div className="mt-4">
              <p className="text-xs text-blue-800 dark:text-blue-300">
                Case assignments are managed from the case detail page. Click on
                a case above to view or manage assignments.
              </p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
